import React, { useEffect } from 'react';
import { classifyError, displayParts, isRetryable, reportShownError } from '../utils/errorCodes';
import './ErrorBanner.scss';

/**
 * Sad MixGoat — the brand mark (same head/horn/headphone geometry as
 * MixGoatLogo) redrawn as a monochrome line icon with a frown and a single
 * tear. Inherits `currentColor` so it themes with the surrounding text.
 */
export const SadGoatIcon = ({ size = 48, className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 512 512"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden="true"
        focusable="false"
    >
        {/* Head */}
        <ellipse cx="256" cy="295" rx="143" ry="163" stroke="currentColor" strokeWidth="24" fill="currentColor" fillOpacity="0.06" />
        {/* Horns */}
        <path d="M140 185 Q80 80 122 40" stroke="currentColor" strokeWidth="34" strokeLinecap="round" fill="none" />
        <path d="M372 185 Q432 80 390 40" stroke="currentColor" strokeWidth="34" strokeLinecap="round" fill="none" />
        {/* Headphones */}
        <path d="M76 250 Q76 120 256 100 Q436 120 436 250" stroke="currentColor" strokeWidth="26" fill="none" strokeLinecap="round" opacity="0.55" />
        <rect x="38" y="225" width="85" height="125" rx="20" fill="currentColor" opacity="0.55" />
        <rect x="389" y="225" width="85" height="125" rx="20" fill="currentColor" opacity="0.55" />
        {/* Downcast eyes — lids drooping toward the outer corners */}
        <path d="M176 262 Q200 280 224 268" stroke="currentColor" strokeWidth="20" strokeLinecap="round" fill="none" />
        <path d="M336 262 Q312 280 288 268" stroke="currentColor" strokeWidth="20" strokeLinecap="round" fill="none" />
        {/* Frown */}
        <path d="M216 372 Q256 344 296 372" stroke="currentColor" strokeWidth="20" strokeLinecap="round" fill="none" />
        {/* Tear */}
        <path d="M330 300 q16 22 0 34 q-16 -12 0 -34" fill="currentColor" opacity="0.7" />
    </svg>
);

/**
 * The one way to render an inline/full error state. Classifies the error via
 * the central map (utils/errorCodes.js), shows the friendly copy with the
 * code as a muted chip, and ships the code + surface to the reporting
 * service (deduped there, so double-reporting with a thunk is harmless).
 *
 *   <ErrorBanner error={searchError} surface="mixpedia_search" onRetry={retry} />
 *
 * variant="banner" (default) — centered block with the sad-goat mark, for
 *   empty content areas (search results, tab bodies).
 * variant="inline" — single compact row, for forms/sidebars/settings panes.
 */
const ErrorBanner = ({ error, surface, onRetry, retryLabel = 'Retry', variant = 'banner', className = '', forceRetry = false }) => {
    const raw = error ? classifyError(error).raw : null;
    useEffect(() => {
        if (error) reportShownError(classifyError(error), { surface: surface || 'error_banner' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raw, surface]);

    if (!error) return null;
    const { text, code } = displayParts(error);
    // Retry appears only when the caller wired a real retry AND the code is
    // one a manual retry can fix — never for rate limits (hammering makes it
    // worse) or auth errors (the renewal loop is already re-exchanging).
    // forceRetry overrides for callers whose retry IS the recovery path even
    // for auth errors (the connect screen re-runs the whole token exchange).
    const showRetry = onRetry && (forceRetry || isRetryable(code || 'UNKNOWN'));

    return (
        <div className={`error-banner error-banner--${variant} ${className}`} role="alert">
            <SadGoatIcon size={variant === 'banner' ? 52 : 22} className="error-banner__icon" />
            <div className="error-banner__content">
                <span className="error-banner__text">{text}</span>
                {code && <span className="error-banner__code">{code}</span>}
            </div>
            {showRetry && (
                <button type="button" className="error-banner__retry" onClick={onRetry}>
                    {retryLabel}
                </button>
            )}
        </div>
    );
};

export default ErrorBanner;
